import React, { useState, useEffect, memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CommentForm from './CommentForm';
import Replies from './Replies';
import { node } from 'prop-types';

const isID = num => !isNaN(parseInt(num));

const fetchComments = async ({ post, parent, offset }) => {
    try {
        let args = `?offset=${offset ?? 0}&parent=${parent ?? 0}&per_page=100&order=asc`;

        if (isID(post)) {
            args += `&post=${post}`;
        }

        const res = await fetch(`${window.location.origin}/wp-json/wp/v2/comments${args}`);

        if (res.ok) {
            const json = await res.json();
            return json.map(({
                author_avatar_urls,
                author_name,
                author_url,
                content,
                date_gmt,
                id,
            }) => ({
                databaseId: id,
                date: formatDate(date_gmt, 'de'),
                content: content.rendered,
                author: {
                    node: {
                        gravatarId: extGravatarId(author_avatar_urls['48']),
                        name: author_name,
                        url: author_url,
                    }
                }
            }));
        }
    } catch (err) {
        console.error(err);
    }
};

function formatDate(date, locale) {
    switch (locale) {
        case 'de':
            return date
                .substring(0, date.length)
                .split('T')[0]
                .split('-')
                .reverse()
                .join('.');
        default:
            return date
                .split('.')
                .reverse()
                .join('-') + 'T00:00:00';
    }
}

function extGravatarId(uri) {
    let id = uri.substring(
        uri.lastIndexOf('/') + 1,
        uri.length - 1
    );

    return id.substring(
        0,
        id.indexOf('?')
    );
}

const toDateISO = dateString =>
    dateString ? (new Date(formatDate(dateString))).toISOString() : '1970-01-19T00:00:00';

function Comments({ data, id, onCountUpdate }) {
    const [comments, setComments] = useState(null);
    const [reply, setReply] = useState(null);

    useEffect(() => {
        if (comments === null) {
            const offset = data !== undefined ? data.length : 0;
            const post = id;

            fetchComments({ post, offset}).then(async fetchedComment => {
                const commentCount = data.length + fetchedComment.length;

                if (commentCount > data.length) {
                    setComments([...fetchedComment, ...data]);
                    onCountUpdate(commentCount);   
                } else {
                    setComments(data);
                }             
            });
        }
    }, [comments])

    const toggleReply = parent => {
        if (reply === parent) {
            setReply();
        } else {
            setReply(parent);
        }
    }

    return (
        <div style={{
            background: '#f9f9f9',
            borderTop: '1px solid #ddd',
            boxShadow: 'var(--card-shadow)',
        }}>
            <div id="comments" className="container container--wide longform comment--container">
                <h3>{data.length > 0 ? 'Kommentare' : 'Schreibe den ersten Kommentar'}</h3>
                <ol className="comment-list">
                    {
                        (comments ?? data)?.map(c => (
                            <li key={(c.node ?? c).databaseId}>
                                <article id={`comment-${c.databaseId}`}>
                                    <header className="comment-meta">
                                        <h4>
                                            <img width={64} src={`/api/static/avatar/${(c.node ?? c).author.node.gravatarId}.png`} alt="avatar" />
                                            <span style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: (c.node ?? c).author.node.name }} />
                                        </h4>
                                        <span>
                                            <time dateTime={(c.node ?? c).date}>
                                                {(c.node ?? c).date}
                                            </time>
                                        </span>
                                    </header>
                                    <section>
                                        <div dangerouslySetInnerHTML={{ __html: (c.node ?? c).content }} />
                                        {(c.node ?? c).replies ? <Replies id={id} data={(c.node ?? c).replies} /> : null}
                                        <button className={reply === (c.node ?? c).databaseId ? 'active' : null} onClick={() => toggleReply((c.node ?? c).databaseId)} style={{ width: '45px' }}>
                                            {reply === (c.node ?? c).databaseId ? <FontAwesomeIcon icon="times" size="lg" /> : <FontAwesomeIcon icon="reply" size="lg" />}
                                        </button>
                                        {reply === (c.node ?? c).databaseId ? <CommentForm id={id} parentId={reply} /> : null}
                                    </section>
                                </article>
                            </li>
                        ))
                    }
                </ol>
                <CommentForm color="transparent" id={id} />
            </div>
        </div>
    );
}

export default memo(Comments);
